import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { fetchAddresses } from '../services/api/addressService';

const STORAGE_KEY = 'dindin.deliveryAddress';
const ORDER_MODE_KEY = 'dindin.orderMode';

function readStoredOrderMode() {
  try {
    const v = String(window.localStorage.getItem(ORDER_MODE_KEY) || '').trim().toLowerCase();
    return v === 'pickup' ? 'pickup' : 'delivery';
  } catch {
    return 'delivery';
  }
}

function writeStoredOrderMode(mode) {
  try {
    window.localStorage.setItem(ORDER_MODE_KEY, mode === 'pickup' ? 'pickup' : 'delivery');
  } catch { /* ignore */ }
}

/**
 * Tracks the most recent geolocation denial / failure across navigations
 * so we don't keep prompting the user inside the same browser session.
 */
const GEO_FAIL_SESSION_KEY = 'dindin.geoAutoFillFailed';

function readStoredAddress() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && String(parsed.address || '').trim()) return parsed;
  } catch { /* ignore */ }
  return null;
}

function writeStoredAddress(addr) {
  try {
    if (!addr) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(addr));
    }
  } catch { /* ignore */ }
}

// --- Google Maps singleton loader ---------------------------------------
// Loads the Maps JS SDK at most once per page. Reuses an existing
// <script data-google-maps-loader="true"> tag if any other component
// (e.g. Banner / Checkout) already injected it.
let googleMapsLoaderPromise = null;
function loadGoogleMaps() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps unavailable (no window)'));
  }
  if (window.google?.maps?.Geocoder) {
    return Promise.resolve(window.google.maps);
  }
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return Promise.reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY'));
  }
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps-loader="true"]');
    if (existing) {
      if (window.google?.maps?.Geocoder) {
        resolve(window.google.maps);
        return;
      }
      existing.addEventListener(
        'load',
        () => resolve(window.google?.maps || null),
        { once: true },
      );
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Google Maps')),
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = 'true';
    script.onload = () => resolve(window.google?.maps || null);
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

/** Reverse-geocode lat/lng into a human-readable address via Google Maps. */
async function reverseGeocodeCoords({ latitude, longitude }) {
  const maps = await loadGoogleMaps();
  if (!maps?.Geocoder) throw new Error('Google Maps Geocoder unavailable');
  const geocoder = new maps.Geocoder();
  const results = await new Promise((resolve, reject) => {
    geocoder.geocode(
      { location: { lat: latitude, lng: longitude } },
      (res, status) => {
        if (status === 'OK' && res?.length) resolve(res);
        else reject(new Error(`Google geocode failed: ${status}`));
      },
    );
  });
  return results[0]?.formatted_address || '';
}

/** Wraps `navigator.geolocation.getCurrentPosition` in a promise. */
function getCurrentPositionOnce() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60_000 },
    );
  });
}

const DeliveryAddressContext = createContext(null);

/**
 * Provides the current delivery address app-wide (header, checkout, restaurant list).
 *
 * Shape:
 *  - currentAddress   { label?, address, coords?: { latitude, longitude } } | null
 *  - setCurrentAddress(addr)
 *  - savedAddresses   [...]   (loaded from API when authenticated)
 *  - refreshAddresses()
 *  - loading          boolean
 *  - guestGeoBootstrapDone  true once guest auto-location attempt finished (success, deny, or skip)
 */
export function DeliveryAddressProvider({ children }) {
  const { isAuthenticated, token } = useAuth();
  const [currentAddress, setCurrentAddressRaw] = useState(() => readStoredAddress());
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guestGeoBootstrapDone, setGuestGeoBootstrapDone] = useState(() => {
    if (typeof window === 'undefined') return true;
    return Boolean(String(readStoredAddress()?.address || '').trim());
  });
  const [orderMode, setOrderModeRaw] = useState(() => readStoredOrderMode());

  const setOrderMode = useCallback((mode) => {
    const next = mode === 'pickup' ? 'pickup' : 'delivery';
    setOrderModeRaw(next);
    writeStoredOrderMode(next);
  }, []);
  const didAutoSelect = useRef(false);
  const didAutoGeolocate = useRef(false);

  const setCurrentAddress = useCallback((addr) => {
    const normalized = addr
      ? {
          id: addr.id ?? null,
          label: addr.label || '',
          address: String(addr.address || addr.address_line || '').trim(),
          source: addr.source || 'manual',
          coords:
            addr.coords || (addr.latitude != null && addr.longitude != null
              ? { latitude: Number(addr.latitude), longitude: Number(addr.longitude) }
              : null),
        }
      : null;
    setCurrentAddressRaw(normalized);
    writeStoredAddress(normalized);
  }, []);

  const loadAddresses = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    setLoading(true);
    try {
      const list = await fetchAddresses(token);
      setSavedAddresses(Array.isArray(list) ? list : []);
      return list;
    } catch {
      setSavedAddresses([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSavedAddresses([]);
      didAutoSelect.current = false;
      didAutoGeolocate.current = false;
      setGuestGeoBootstrapDone(Boolean(String(readStoredAddress()?.address || '').trim()));
      return;
    }
    (async () => {
      const list = await loadAddresses();
      if (didAutoSelect.current || !Array.isArray(list) || !list.length) return;
      didAutoSelect.current = true;
      const defaultAddr = list.find((a) => a.is_default) || list[0];
      // Override a previously auto-detected "current location" guess once the
      // user's real saved default arrives — but never override a manual pick.
      const canOverride =
        !currentAddress?.address || currentAddress?.source === 'current';
      if (defaultAddr && canOverride) {
        setCurrentAddress({ ...defaultAddr, source: 'default' });
      }
    })();
  }, [isAuthenticated, loadAddresses, setCurrentAddress, currentAddress?.address, currentAddress?.source]);

  // --- Auto-fill from device geolocation --------------------------------
  // Runs once per provider lifecycle. If the user has no address set yet
  // (no stored value, no saved default), try the browser geolocation API
  // and reverse-geocode the coords so the header / banner / restaurant
  // list all start with the user's actual current address.
  const currentAddressRef = useRef(currentAddress);
  useEffect(() => {
    currentAddressRef.current = currentAddress;
  }, [currentAddress]);

  useEffect(() => {
    if (isAuthenticated) {
      setGuestGeoBootstrapDone(true);
      return;
    }

    if (didAutoGeolocate.current) return;
    if (String(currentAddressRef.current?.address || '').trim()) {
      setGuestGeoBootstrapDone(true);
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGuestGeoBootstrapDone(true);
      return;
    }
    // Skip if we already tried (and failed / were denied) earlier this session.
    try {
      if (window.sessionStorage?.getItem(GEO_FAIL_SESSION_KEY) === '1') {
        setGuestGeoBootstrapDone(true);
        return;
      }
    } catch { /* ignore */ }

    didAutoGeolocate.current = true;
    let cancelled = false;

    (async () => {
      try {
        const pos = await getCurrentPositionOnce();
        if (cancelled || String(currentAddressRef.current?.address || '').trim()) return;
        const latitude = pos.coords?.latitude;
        const longitude = pos.coords?.longitude;
        if (latitude == null || longitude == null) return;

        let address = '';
        try {
          address = await reverseGeocodeCoords({ latitude, longitude });
        } catch { /* fall through — coords still useful for search radius */ }

        if (cancelled || String(currentAddressRef.current?.address || '').trim()) return;
        const line = String(address || '').trim() || 'Current location';
        setCurrentAddress({
          address: line,
          coords: { latitude, longitude },
          source: 'current',
        });
      } catch {
        // User denied permission, timed out, or no signal.
        try { window.sessionStorage?.setItem(GEO_FAIL_SESSION_KEY, '1'); } catch { /* ignore */ }
      } finally {
        if (!cancelled) setGuestGeoBootstrapDone(true);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated, setCurrentAddress]);

  const value = useMemo(
    () => ({
      currentAddress,
      setCurrentAddress,
      savedAddresses,
      refreshAddresses: loadAddresses,
      loading,
      guestGeoBootstrapDone,
      orderMode,
      setOrderMode,
      isPickup: orderMode === 'pickup',
    }),
    [currentAddress, setCurrentAddress, savedAddresses, loadAddresses, loading, guestGeoBootstrapDone, orderMode, setOrderMode]
  );

  return (
    <DeliveryAddressContext.Provider value={value}>
      {children}
    </DeliveryAddressContext.Provider>
  );
}

export function useDeliveryAddress() {
  const ctx = useContext(DeliveryAddressContext);
  if (!ctx) throw new Error('useDeliveryAddress must be used within DeliveryAddressProvider');
  return ctx;
}
